import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FavoriteFacadeService } from '../../../services/favorites/favorite-facade.service';
import { FavoriteSlot } from '../../../services/favorites/favorite.models';

@Component({
  selector: 'app-favorite-manager',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './favorite-manager.component.html',
  styleUrl: './favorite-manager.component.scss',
})
export class FavoriteManagerComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  slots: FavoriteSlot[] = [];
  nameSortDirection: 'asc' | 'desc' = 'asc';
  loading = false;

  // Blocks the whole modal while a rename/overwrite/delete write is in progress
  isBlocking = false;
  // Message shown in the blocking overlay, dynamic per operation
  blockingMessage = '';

  // Inline edit state
  editingSlot: string | null = null;
  editValue = '';

  // Rename overwrite confirmation (name already used by another slot)
  renameConfirmVisible = false;
  pendingRename: { slot: FavoriteSlot; targetSlot: FavoriteSlot; newName: string } | null = null;

  // Delete confirmation state
  deleteDialogVisible = false;
  deleteSlot: FavoriteSlot | null = null;

  // Toast message
  toastMessage = '';
  toastVisible = false;
  private toastTimeout: any;

  // Per-row loading
  rowLoading: Set<string> = new Set();

  private subscriptions: Subscription[] = [];

  constructor(private facade: FavoriteFacadeService) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.facade.slots$.subscribe(slots => this.slots = slots),
      this.facade.loading$.subscribe(loading => this.loading = loading),
    );
    // No reload here — facade pre-loads at app init. Modal just reads cache.
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
  }

  get displayedSlots(): FavoriteSlot[] {
    const direction = this.nameSortDirection === 'asc' ? 1 : -1;

    return [...this.slots].sort((first, second) => {
      const firstOccupied = this.isSlotOccupied(first);
      const secondOccupied = this.isSlotOccupied(second);

      if (firstOccupied !== secondOccupied) return firstOccupied ? -1 : 1;
      if (!firstOccupied) return Number(first.slno) - Number(second.slno);

      const nameComparison = (first.UIName ?? '').localeCompare(second.UIName ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      });

      return nameComparison === 0
        ? Number(first.slno) - Number(second.slno)
        : nameComparison * direction;
    });
  }

  toggleNameSort(): void {
    this.nameSortDirection = this.nameSortDirection === 'asc' ? 'desc' : 'asc';
  }

  close(): void {
    if (this.isBlocking) return; // Block closing while a write is in progress
    this.closed.emit();
  }

  // --- Actions ---

  onImport(slot: FavoriteSlot): void {
    const result = this.facade.importFavorite(slot);
    this.showToast(result.message);
    if (result.success) {
      this.close();
    }
  }

  // --- Inline edit ---

  startInlineEdit(slot: FavoriteSlot): void {
    this.editingSlot = slot.slno;
    this.editValue = slot.UIName || '';
  }

  confirmInlineEdit(): void {
    if (!this.editingSlot || !this.editValue.trim()) return;
    const slot = this.slots.find(s => s.slno === this.editingSlot);
    if (!slot) return;

    // Detect another slot already using this name -> confirm overwrite.
    const targetSlot = this.facade.findOccupiedSlotByName(this.editValue, slot.slno);
    if (targetSlot) {
      this.pendingRename = { slot, targetSlot, newName: this.editValue };
      this.renameConfirmVisible = true;
      return;
    }

    this.doRename(slot, this.editValue);
  }

  onRenameChange(value: string): void {
    this.editValue = this.facade.stripForbiddenNameChars(value);
  }

  copyShareLink(slot: FavoriteSlot): void {
    if (!slot.shareableLink) return;
    const link = slot.shareableLink;

    // Async Clipboard API is only available in secure contexts (https/localhost).
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(
        () => this.showToast('Share link copied to clipboard.'),
        () => this.showToast(this.fallbackCopy(link)),
      );
      return;
    }

    this.showToast(this.fallbackCopy(link));
  }

  // Legacy copy path for insecure contexts where navigator.clipboard is unavailable.
  private fallbackCopy(text: string): string {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok ? 'Share link copied to clipboard.' : 'Could not copy share link.';
    } catch {
      return 'Could not copy share link.';
    }
  }

  private doRename(slot: FavoriteSlot, newName: string): void {
    this.startBlocking('Saving...');
    this.rowLoading.add(slot.slno);
    const slno = slot.slno;
    this.editingSlot = null;
    this.facade.renameFavorite(slot, newName).subscribe(result => {
      this.rowLoading.delete(slno);
      this.stopBlocking();
      this.showToast(result.message);
      this.editValue = '';
    });
  }

  confirmRenameOverwrite(): void {
    if (!this.pendingRename) return;
    const { slot, targetSlot, newName } = this.pendingRename;
    this.renameConfirmVisible = false;
    this.pendingRename = null;
    this.startBlocking('Saving...');
    this.rowLoading.add(slot.slno);
    const slno = slot.slno;
    this.editingSlot = null;
    this.facade.overwriteRename(slot, targetSlot, newName).subscribe(result => {
      this.rowLoading.delete(slno);
      this.stopBlocking();
      this.showToast(result.message);
      this.editValue = '';
    });
  }

  cancelRenameOverwrite(): void {
    this.renameConfirmVisible = false;
    this.pendingRename = null;
  }

  cancelInlineEdit(): void {
    this.editingSlot = null;
    this.editValue = '';
  }

  // --- Delete ---

  onDelete(slot: FavoriteSlot): void {
    this.deleteSlot = slot;
    this.deleteDialogVisible = true;
  }

  onConfirmDelete(): void {
    if (!this.deleteSlot) return;
    this.startBlocking('Deleting...');
    this.rowLoading.add(this.deleteSlot.slno);
    this.deleteDialogVisible = false;
    this.facade.deleteFavorite(this.deleteSlot).subscribe(result => {
      if (this.deleteSlot) this.rowLoading.delete(this.deleteSlot.slno);
      this.stopBlocking();
      this.showToast(result.message);
      this.deleteSlot = null;
    });
  }

  onCancelDelete(): void {
    this.deleteDialogVisible = false;
    this.deleteSlot = null;
  }

  // --- Helpers ---

  private startBlocking(message: string): void {
    this.blockingMessage = message;
    this.isBlocking = true;
  }

  private stopBlocking(): void {
    this.isBlocking = false;
    this.blockingMessage = '';
  }

  isSlotOccupied(slot: FavoriteSlot): boolean {
    return slot.Name !== null;
  }

  isRowLoading(slot: FavoriteSlot): boolean {
    return this.rowLoading.has(slot.slno);
  }

  private showToast(message: string): void {
    this.toastMessage = message;
    this.toastVisible = true;
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => {
      this.toastVisible = false;
    }, 3000);
  }
}
